"use client";

import { motion, useReducedMotion } from "motion/react";
import type { Variants } from "motion/react";
import { ArrowRight, Clock, Calendar } from "lucide-react";
import Image from "next/image";
import { cn } from "@/lib/utils";
import { postHref, type BlogPost as CmsBlogPost } from "@/lib/blog";

// ── Types ───────────────────────────────────────────────────────────────────
// Local shape the card renders — mapped from the live `BlogPost` (lib/blog.ts)
// in the section component below.

interface BlogPost {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  readTime: string;
  date: string;
  imageSrc: string;
  imageAlt: string;
}

// ── Motion ──────────────────────────────────────────────────────────────────

function fadeUp(reduce: boolean): Variants {
  return {
    hidden: { opacity: 0, y: reduce ? 0 : 22 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.55, ease: [0.2, 0, 0, 1] },
    },
  };
}

const container: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1, delayChildren: 0.05 } },
};

// ── Blog Card ───────────────────────────────────────────────────────────────

function BlogCard({ post }: { post: BlogPost }) {
  return (
    <motion.a
      variants={fadeUp(false)}
      href={postHref(post)}
      aria-label={post.title}
      className={cn(
        "group flex w-[82vw] min-w-[82vw] max-w-[380px] snap-start cursor-pointer flex-col overflow-hidden rounded-card border border-[#e3ecfb] bg-white md:w-auto md:min-w-0 md:max-w-none",
        "transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
      )}
    >
      {/* Blog image */}
      <div className="relative h-48 overflow-hidden">
        <Image
          src={post.imageSrc}
          alt={post.imageAlt}
          fill
          sizes="(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw"
          className="object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-900/35 via-primary-900/5 to-transparent" />
        {/* Category pill */}
        <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-[11px] font-semibold text-primary-800 backdrop-blur-sm">
          {post.category}
        </span>
      </div>

      {/* Content */}
      <div className="flex flex-1 flex-col gap-3 p-5">
        <h3 className="line-clamp-2 text-lg font-bold leading-snug tracking-[-0.01em] text-primary-900">
          {post.title}
        </h3>
        <p className="line-clamp-2 text-sm leading-relaxed text-text-body">
          {post.excerpt}
        </p>

        {/* Meta */}
        <div className="mt-auto flex items-center gap-4 border-t border-[#e3ecfb] pt-4 text-xs text-text-muted">
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" />
            {post.readTime}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5" />
            {post.date}
          </span>
        </div>
      </div>
    </motion.a>
  );
}

// ── Section ─────────────────────────────────────────────────────────────────

export function BlogSection({ posts }: { posts: CmsBlogPost[] }) {
  const reduce = useReducedMotion() ?? false;

  // Map the live CMS posts (lib/blog.ts BlogPost) onto this section's local
  // card shape. There's no per-post read time from the CMS, so it's a fixed
  // display default — same approximation BlogArticle.tsx already uses.
  const blogPosts: BlogPost[] = posts.slice(0, 3).map((post) => ({
    slug: post.slug,
    title: post.title,
    excerpt: post.excerpt,
    category: post.category,
    readTime: "5 min read",
    date: post.date,
    imageSrc: post.image,
    imageAlt: post.title,
  }));

  if (blogPosts.length === 0) return null;

  return (
    <motion.section
      className="border-t border-[#e3ecfb] bg-white pb-16 pt-16 sm:pb-20 sm:pt-20 lg:pb-24 lg:pt-24"
      aria-labelledby="blog-heading"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.15 }}
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        {/* Header */}
        <motion.div
          variants={fadeUp(reduce)}
          className="mb-12 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between"
        >
          <div>
            <span className="text-xs font-semibold uppercase tracking-[0.14em] text-primary-500">
              From the Blog
            </span>
            <h2
              id="blog-heading"
              className="mt-3 text-[clamp(1.75rem,3.5vw,2.5rem)] font-semibold leading-[1.15] tracking-[-0.015em] text-primary-900"
            >
              Gold, money, markets
            </h2>
          </div>
          <a
            href="/blogs"
            className="group/link inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 transition-colors hover:text-primary-800"
          >
            View all blogs
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover/link:translate-x-0.5" />
          </a>
        </motion.div>

        {/* Mobile: swipeable carousel. Desktop: three card grid. */}
        <motion.div
          variants={container}
          className="-mx-5 flex snap-x snap-mandatory gap-4 overflow-x-auto px-5 pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden md:mx-0 md:grid md:grid-cols-2 md:gap-6 md:overflow-visible md:px-0 md:pb-0 lg:grid-cols-3"
          role="region"
          aria-label="Latest blog posts"
        >
          {blogPosts.map((post) => (
            <BlogCard key={post.slug} post={post} />
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
}
